import { useMemo } from "react";

import logoRaw from "./logo.svg?raw";
import markRaw from "./mark.svg?raw";

// The generated SVGs carry internal ids (masks, clip-paths). Inlining the same
// SVG more than once per page would duplicate those ids and cross-wire the
// `url(#…)` references, so each instance gets its own id namespace.
let counter = 0;
function uniquifyIds(svg: string, suffix: string): string {
  const ids = new Set<string>();
  svg.replace(/\bid="([^"]+)"/g, (_m, id: string) => (ids.add(id), _m));
  let out = svg;
  for (const id of ids) {
    const esc = id.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    out = out
      .replace(new RegExp(`id="${esc}"`, "g"), `id="${id}-${suffix}"`)
      .replace(new RegExp(`url\\(#${esc}\\)`, "g"), `url(#${id}-${suffix})`);
  }
  return out;
}

function useInlineSvg(raw: string): string {
  // eslint-disable-next-line react-hooks/exhaustive-deps
  return useMemo(() => uniquifyIds(raw, `b${counter++}`), []);
}

/** The full GraphView monogram, inlined so its colour classes recolour live from
 * the `--logo-*` CSS variables (see brand/themes.ts). Sized by the wrapper's
 * height; the SVG fills it (`.brand-svg svg` in styles.css). */
export function BrandLogo({ className }: { className?: string }) {
  const html = useInlineSvg(logoRaw);
  return (
    <span
      className={`brand-svg ${className ?? ""}`}
      role="img"
      aria-label="GraphView"
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
}

/** The square brand mark (favicon art). White is transparent here by design. */
export function BrandMark({ className }: { className?: string }) {
  const html = useInlineSvg(markRaw);
  return (
    <span
      className={`brand-svg ${className ?? ""}`}
      role="img"
      aria-label="GraphView"
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
}
